import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Pagination,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

interface Email {
  id: string;
  subject: string;
  sender: string;
  summary: string;
  classification: 'actionable' | 'non-actionable';
  priority: 'urgent' | 'high' | 'medium' | 'low';
  timestamp: string;
  hasDeadline: boolean;
  followUpRequired: boolean;
}

const EmailList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const emailsPerPage = 10;

  // Mock email data
  const mockEmails: Email[] = [
    {
      id: '1',
      subject: 'Urgent: Project Deadline Approaching',
      sender: 'project.manager@company.com',
      summary: 'The quarterly project deadline is tomorrow. Please submit all deliverables by 5 PM.',
      classification: 'actionable',
      priority: 'urgent',
      timestamp: '2024-01-15T10:30:00Z',
      hasDeadline: true,
      followUpRequired: false,
    },
    {
      id: '2',
      subject: 'Weekly Newsletter - Tech Updates',
      sender: 'newsletter@techcompany.com',
      summary: 'Latest technology trends and updates from the industry.',
      classification: 'non-actionable',
      priority: 'low',
      timestamp: '2024-01-15T09:15:00Z',
      hasDeadline: false,
      followUpRequired: false,
    },
    {
      id: '3',
      subject: 'Meeting Request: Budget Review',
      sender: 'finance.director@company.com',
      summary: 'Please confirm your availability for the budget review meeting next week.',
      classification: 'actionable',
      priority: 'high',
      timestamp: '2024-01-15T08:45:00Z',
      hasDeadline: false,
      followUpRequired: true,
    },
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const getClassificationColor = (classification: string) => {
    return classification === 'actionable' ? 'primary' : 'secondary';
  };

  const filteredEmails = mockEmails.filter((email) => {
    const matchesSearch = email.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         email.sender.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterBy === 'all') return matchesSearch;
    if (filterBy === 'actionable') return matchesSearch && email.classification === 'actionable';
    if (filterBy === 'non-actionable') return matchesSearch && email.classification === 'non-actionable';
    if (filterBy === 'deadlines') return matchesSearch && email.hasDeadline;
    if (filterBy === 'follow-ups') return matchesSearch && email.followUpRequired;
    
    return matchesSearch;
  });

  const totalPages = Math.ceil(filteredEmails.length / emailsPerPage);
  const startIndex = (currentPage - 1) * emailsPerPage;
  const paginatedEmails = filteredEmails.slice(startIndex, startIndex + emailsPerPage);

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Email Management
      </Typography>

      {/* Search and Filter Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            placeholder="Search emails..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Filter by</InputLabel>
            <Select
              value={filterBy}
              label="Filter by"
              onChange={(e) => setFilterBy(e.target.value)}
            >
              <MenuItem value="all">All Emails</MenuItem>
              <MenuItem value="actionable">Actionable</MenuItem>
              <MenuItem value="non-actionable">Non-Actionable</MenuItem>
              <MenuItem value="deadlines">Has Deadlines</MenuItem>
              <MenuItem value="follow-ups">Needs Follow-up</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Email List */}
      <List>
        {paginatedEmails.map((email) => (
          <ListItem key={email.id} sx={{ p: 0, mb: 2 }}>
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Typography variant="h6" component="div">
                    {email.subject}
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Chip
                      label={email.classification}
                      color={getClassificationColor(email.classification) as any}
                      size="small"
                    />
                    <Chip
                      label={email.priority}
                      color={getPriorityColor(email.priority) as any}
                      size="small"
                    />
                    {email.hasDeadline && (
                      <Chip label="Deadline" color="error" size="small" />
                    )}
                    {email.followUpRequired && (
                      <Chip label="Follow-up" color="warning" size="small" />
                    )}
                  </Box>
                </Box>
                
                <Typography color="textSecondary" gutterBottom>
                  From: {email.sender} • {formatTimestamp(email.timestamp)}
                </Typography>
                
                <Typography variant="body2" color="textSecondary">
                  {email.summary}
                </Typography>
              </CardContent>
            </Card>
          </ListItem>
        ))}
      </List>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
          />
        </Box>
      )}

      {filteredEmails.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="textSecondary">
            No emails found matching your criteria
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EmailList;